import React, { useState } from "react";
import { AuthenticatedLayout } from "../../components/layout/AuthenticatedLayout";
import { useNavigate } from "react-router-dom";

export default function CreateEventPage() {
  const navigate = useNavigate();

  const [eventData, setEventData] = useState({
    name: "",
    date: "",
    time: "",
    location: "",
    description: "",
    maxParticipants: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    image: null as File | null
  });

  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEventData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setEventData(prev => ({ ...prev, image: file }));

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Creating event with data:", eventData);
    // Implement event creation logic here

    // Navigate back to events page after creation
    navigate("/events");
  };

  return (
    <AuthenticatedLayout>
      <div className="w-full h-full bg-[#FBF6F1] flex flex-col">
        {/* Header */}
        <div className="w-full h-14 p-4 inline-flex justify-between items-center">
          <div className="size-7 relative overflow-hidden" onClick={() => navigate(-1)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M17.5 7L10.5 14L17.5 21" stroke="#1E3A3A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="justify-start text-[#1E3A3A] text-2xl font-medium font-manrope leading-7">Tạo sự kiện mới</div>
          <div className="size-7 relative overflow-hidden opacity-0">
            {/* Placeholder for alignment */}
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-auto p-4">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Image upload */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium font-sora text-[#1E3A3A]">Ảnh sự kiện</label>
              <div
                className="w-full h-40 bg-[#FBF6F1] rounded-lg border border-gray-200 flex items-center justify-center cursor-pointer relative"
                onClick={() => document.getElementById("event-image")?.click()}
              >
                {previewImage ? (
                  <img src={previewImage} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M4 16L8.586 11.414C8.96106 11.0391 9.46967 10.8284 10 10.8284C10.5303 10.8284 11.0389 11.0391 11.414 11.414L16 16M14 14L15.586 12.414C15.9611 12.0391 16.4697 11.8284 17 11.8284C17.5303 11.8284 18.0389 12.0391 18.414 12.414L20 14M14 8H14.01M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="#6F6F6B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-normal font-sora text-[#6F6F6B]">Tải lên ảnh sự kiện</span>
                  </div>
                )}
                <input
                  type="file"
                  id="event-image"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>
            </div>

            {/* Event name */}
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-sm font-medium font-sora text-[#1E3A3A]">Tên sự kiện</label>
              <input
                type="text"
                id="name"
                name="name"
                value={eventData.name}
                onChange={handleChange}
                placeholder="Nhập tên sự kiện"
                className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                required
              />
            </div>

            {/* Date and time */}
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-2">
                <label htmlFor="date" className="text-sm font-medium font-sora text-[#1E3A3A]">Ngày</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={eventData.date}
                  onChange={handleChange}
                  className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                  required
                />
              </div>
              <div className="flex-1 flex flex-col gap-2">
                <label htmlFor="time" className="text-sm font-medium font-sora text-[#1E3A3A]">Giờ</label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  value={eventData.time}
                  onChange={handleChange}
                  className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                  required
                />
              </div>
            </div>

            {/* Location */}
            <div className="flex flex-col gap-2">
              <label htmlFor="location" className="text-sm font-medium font-sora text-[#1E3A3A]">Địa điểm</label>
              <input
                type="text"
                id="location"
                name="location"
                value={eventData.location}
                onChange={handleChange}
                placeholder="Nhập địa điểm"
                className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                required
              />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-2">
              <label htmlFor="description" className="text-sm font-medium font-sora text-[#1E3A3A]">Mô tả</label>
              <textarea
                id="description"
                name="description"
                value={eventData.description}
                onChange={handleChange}
                placeholder="Nhập mô tả sự kiện"
                className="w-full h-32 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A] resize-none"
                required
              />
            </div>

            {/* Max participants */}
            <div className="flex flex-col gap-2">
              <label htmlFor="maxParticipants" className="text-sm font-medium font-sora text-[#1E3A3A]">Số người tham gia tối đa</label>
              <input
                type="number"
                id="maxParticipants"
                name="maxParticipants"
                value={eventData.maxParticipants}
                onChange={handleChange}
                placeholder="Nhập số người tham gia tối đa"
                className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                required
              />
            </div>

            {/* Contact information */}
            <div className="flex flex-col gap-2">
              <h2 className="text-lg font-medium font-sora text-[#1E3A3A]">Thông tin liên hệ</h2>

              <div className="flex flex-col gap-2">
                <label htmlFor="contactName" className="text-sm font-medium font-sora text-[#1E3A3A]">Tên người tổ chức</label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={eventData.contactName}
                  onChange={handleChange}
                  placeholder="Nhập tên người tổ chức"
                  className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contactPhone" className="text-sm font-medium font-sora text-[#1E3A3A]">Số điện thoại</label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={eventData.contactPhone}
                  onChange={handleChange}
                  placeholder="Nhập số điện thoại"
                  className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                  required
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contactEmail" className="text-sm font-medium font-sora text-[#1E3A3A]">Email</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={eventData.contactEmail}
                  onChange={handleChange}
                  placeholder="Nhập email"
                  className="w-full h-12 px-3 py-2 bg-[#FBF6F1] rounded-lg border border-gray-200 text-sm font-normal font-sora text-[#1E3A3A]"
                  required
                />
              </div>
            </div>

            {/* Submit button */}
            <div className="mt-4">
              <button
                type="submit"
                className="w-full py-3.5 bg-[#E06448] rounded-full flex justify-center items-center gap-2.5"
              >
                <div className="text-center text-[#F4E7DA] text-sm font-medium font-sora leading-none">
                  Tạo sự kiện
                </div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
